<template>
  <div>
    <b-overlay :show="loading" rounded="sm" no-fade>
      <b-row class="mb-1">
        <b-col cols="12" md="7" class="d-flex align-items-center">
          <h3 class="ml-1 mb-0">Governance Events</h3>
        </b-col>
        <b-col cols="12" md="5" class="d-flex align-items-center justify-content-end">
          <b-form-checkbox v-model="showPast" switch class="mr-1">Show past</b-form-checkbox>
          <b-form-input v-model="filter" class="d-inline-block" placeholder="Search..." style="max-width: 220px;" />
        </b-col>
      </b-row>

      <b-table
        striped
        hover
        bordered
        responsive="sm"
        :small="true"
        :items="rows"
        :fields="fields"
        :filter="filter"
        show-empty
        empty-text="No governance events"
        class="table table-sm"
      >
        <template #cell(start_at)="{ item }">
          <div v-for="(p, idx) in govParts(item)" :key="p.id || idx" class="text-nowrap">
            <small v-if="govParts(item).length > 1" class="text-muted">Part {{ p.sequence_no || idx + 1 }}: </small>{{ govFormatPartRange(p, item.is_all_day) }}
          </div>
        </template>
        <template #cell(title)="{ item }">
          <span class="font-weight-600">{{ item.title }}</span>
          <b-badge v-if="item.is_admin" variant="light-primary" class="ml-50">Administrator</b-badge>
          <small class="d-block text-muted">
            {{ govTypeLabel(item.event_type) }}<span v-if="item.working_group"> · {{ item.working_group.group_name }}</span>
          </small>
        </template>
        <template #cell(where)="{ item }">
          <div v-for="(p, idx) in govParts(item)" :key="p.id || idx">
            <a v-if="p.meeting_url && (!p.location_label || p.location_label === 'Online')" :href="govHref(p.meeting_url)" target="_blank" rel="noopener">Online</a>
            <span v-else-if="p.location_label">{{ p.location_label }}</span>
            <span v-else class="text-muted">-</span>
          </div>
        </template>
        <template #cell(status)="{ item }">
          <b-badge :variant="govStatusVariant(item.status)" class="text-capitalize">{{ item.status }}</b-badge>
        </template>
        <template #cell(my_response)="{ item }">
          <div v-if="item.my_response" class="text-nowrap">
            <b-badge :variant="govResponseVariant(item.my_response)" class="text-capitalize mr-50">{{ item.my_response }}</b-badge>
            <template v-if="item.status === 'scheduled'">
              <b-button size="sm" variant="flat-success" class="btn-icon" title="Accept" @click="respond(item, 'accepted')">
                <feather-icon icon="CheckIcon" />
              </b-button>
              <b-button size="sm" variant="flat-warning" class="btn-icon" title="Tentative" @click="respond(item, 'tentative')">
                <feather-icon icon="HelpCircleIcon" />
              </b-button>
              <b-button size="sm" variant="flat-danger" class="btn-icon" title="Decline" @click="respond(item, 'declined')">
                <feather-icon icon="XIcon" />
              </b-button>
            </template>
          </div>
          <span v-else class="text-muted">-</span>
        </template>
        <template #cell(materials)="{ item }">
          <b-button
            v-for="m in item.materials"
            :key="m.id"
            size="sm"
            variant="flat-primary"
            class="btn-icon"
            :title="`${govCapitalize(m.kind)}: ${m.title}`"
            @click="govDownloadMaterial(m)"
          >
            <feather-icon :icon="m.kind === 'agenda' ? 'ListIcon' : m.kind === 'minutes' ? 'FileTextIcon' : 'PaperclipIcon'" />
          </b-button>
          <span v-if="!item.materials || !item.materials.length" class="text-muted">-</span>
        </template>
        <template #cell(actions)="{ item }">
          <div class="text-nowrap">
            <b-button size="sm" variant="outline-primary" @click="openDetails(item.id)">View</b-button>
            <b-button
              v-if="item.my_response && item.status !== 'cancelled'"
              size="sm"
              :variant="item.my_special_needs ? 'success' : 'outline-secondary'"
              class="ml-50"
              :title="item.my_special_needs ? 'Your additional requirements: ' + item.my_special_needs : 'Add your additional requirements'"
              @click="openDetails(item.id)"
            >
              <i class="fa-solid fa-heart-pulse mr-25"></i>Requirements
            </b-button>
          </div>
        </template>
      </b-table>
    </b-overlay>

    <governance-event-details-modal
      modal-id="dashboard-governance-details"
      :event-id="selectedEventId"
      :show-invitees="hasAccess"
      @responded="onResponded"
      @requirements-saved="onRequirementsSaved"
      @hidden="selectedEventId = null"
    />
  </div>
</template>

<script>
import store from "@/store";
import governanceMixin, { registerGovernanceStore } from "./governanceMixin";
import GovernanceEventDetailsModal from "./GovernanceEventDetailsModal.vue";

/**
 * Dashboard tab listing the governance events the signed-in user can see:
 * everything for Chairpersons / Directors, invited events only for others.
 */
export default {
  name: "GovernanceDashboardTab",
  components: { GovernanceEventDetailsModal },
  mixins: [governanceMixin],
  props: {
    events: { type: Array, default: () => [] },
    past: { type: Array, default: () => [] },
    hasAccess: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      filter: null,
      showPast: false,
      selectedEventId: null,
      fields: [
        { key: "start_at", label: "When" },
        { key: "title", label: "Event" },
        { key: "where", label: "Where" },
        { key: "status", label: "Status" },
        { key: "my_response", label: "My Response" },
        { key: "materials", label: "Materials", class: "text-center" },
        { key: "actions", label: "", class: "text-center" },
      ],
    };
  },
  computed: {
    rows() {
      return this.showPast ? [...this.events, ...this.past] : this.events;
    },
  },
  created() {
    registerGovernanceStore();
  },
  methods: {
    href(url) {
      return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    },
    openDetails(id) {
      this.selectedEventId = id;
      this.$nextTick(() => this.$bvModal.show("dashboard-governance-details"));
    },
    respond(item, response) {
      this.loading = true;
      store
        .dispatch("governance/respond", { id: item.id, response })
        .then(() => {
          item.my_response = response;
          this.govToast("Response saved");
        })
        .catch((error) => this.govToast("Could not save response", "danger", this.govErrorMessage(error)))
        .finally(() => {
          this.loading = false;
        });
    },
    onResponded({ id, response }) {
      const row = [...this.events, ...this.past].find((e) => e.id === id);
      if (row) row.my_response = response;
    },
    onRequirementsSaved({ id, special_needs }) {
      const row = [...this.events, ...this.past].find((e) => e.id === id);
      if (row) this.$set(row, "my_special_needs", special_needs);
    },
  },
};
</script>
